import { FC, useState, MouseEvent, useEffect, useMemo } from 'react';
import {
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { ProUpload, ProSpace, ProNotification } from '../../antd';
import Helper from '../../Helper';
import classNames from 'classnames';
import { getExtraData } from '../utils';
import { getTimeRandom } from '../../utils/dataUtils';
import { AliyunUploadImageProps, EXPIRE_TIME } from '../interface';
import {
  UploadFile,
  UploadChangeParam,
  RcFile,
} from 'antd/lib/upload/interface';
import { IMAGE_THUMB_NAME } from '../../constants/constant';

const Plupload: FC<AliyunUploadImageProps> = props => {
  const {
    scene,
    OSSData,
    uploadIcon,
    uploadText,
    disabled,
    size,
    style,
    wrapperClassName,
    fileList,
    hideImage,
    onChange,
    onReset,
    beforeUpload,
    getPopupContainer,
    ...restProps
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>();
  useEffect(() => {
    if (fileList) setImageUrl(fileList?.[0]?.thumbUrl);
  }, [fileList]);
  /** 上传前数据处理 */
  const handleBeforeUpload = async (file: any, FileList: RcFile[]) => {
    if (beforeUpload) {
      const beforeData = beforeUpload(file, FileList);
      if (beforeData === false || beforeData === ProUpload.LIST_IGNORE)
        return ProUpload.LIST_IGNORE;
    }
    const { dir, expire, endpoint } = OSSData;
    const nowTime = Date.now() + EXPIRE_TIME;
    const expireTime = Number(expire) * 1000;
    if (expireTime < nowTime) {
      scene && window.sessionStorage.setItem(scene, '');
      await onReset?.();
    }
    const suffix = file.name.slice(file.name.lastIndexOf('.'));
    const randomFlag = getTimeRandom();
    const filename = `${randomFlag}${suffix}`;
    const url: string = dir + filename;
    file.relativePath = `/${url}`;
    file.thumbUrl = `${endpoint}/${dir}${randomFlag}${IMAGE_THUMB_NAME}${suffix}`;
    file.url = `${endpoint}/${url}`;
    return file;
  };
  /** 获取参数 */
  const handleExtraData = (file: UploadFile<any>) => {
    return getExtraData(file, OSSData);
  };
  /** 上传文件改变时 */
  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    const { file, fileList } = info;
    if (file.status === 'uploading') {
      setLoading(true);
    }
    if (file.status === 'error') {
      ProNotification.warning({
        message: `${file.name}上传失败`,
      });
      setLoading(false);
      setImageUrl('');
      onChange?.();
      return false;
    }
    if (file.status === 'done') {
      setLoading(false);
      setImageUrl(file.thumbUrl);
      onChange?.([...fileList]);
      return false;
    }
    // onChange?.([restFile]);
  };
  // 点击删除图标
  const handleClick = (event: MouseEvent<HTMLSpanElement>) => {
    onChange?.();
    setImageUrl('');
    event.preventDefault();
    event.stopPropagation();
  };
  // 点击查看大图图标
  const handlePreviewImage = (event: MouseEvent<HTMLSpanElement>) => {
    if (imageUrl) {
      const originalImageSrc = imageUrl.replace?.(IMAGE_THUMB_NAME, '');
      Helper.previewImage({
        getPopupContainer,
        current: originalImageSrc,
        images: [originalImageSrc],
      });
    }
    event.preventDefault();
    event.stopPropagation();
  };
  /** 操作按钮 */
  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : uploadIcon}
      {(uploadText && size !== 'small') ? <div>{uploadText}</div> : null}
    </div>
  );
  const uploadClassName = useMemo(() => {
    return classNames(wrapperClassName || 'yb-upload', {
      'yb-upload-small': size === 'small',
    });
  }, [size, wrapperClassName]);
  
  return (
    <div
      className={size === 'default' ? '' : 'yb-upload-auto-size'}
      style={style}
    >
      <ProUpload
        maxCount={1}
        className={uploadClassName}
        disabled={disabled}
        multiple={false}
        {...restProps}
        onChange={handleChange}
        beforeUpload={handleBeforeUpload}
        data={handleExtraData}
        action={OSSData?.host}
      >
        {!hideImage && imageUrl ? (
          <div className="yb-upload-image">
            <div className="yb-upload-image-delete">
              <ProSpace>
                <EyeOutlined
                  className="yb-upload-image-delete-big"
                  onClick={handlePreviewImage}
                />
                {!disabled && (
                  <DeleteOutlined
                    className="yb-upload-image-delete-icon"
                    onClick={handleClick}
                  />
                )}
              </ProSpace>
            </div>
            <img
              src={imageUrl}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        ) : (
          uploadButton
        )}
      </ProUpload>
    </div>
  );
};

Plupload.defaultProps = {
  listType: 'picture-card',
  uploadText: '点击上传',
  size: 'default',
  uploadIcon: <UploadOutlined />,
  showUploadList: false,
  name: 'file',
};

export default Plupload;
